import {createBrowserHistory as createHistory} from "history";
import {EditBase, useDataProvider, useEditContext, useNotify, useRedirect} from "react-admin";
import {Button, Card, Collapse, DatePicker, Form, Input, Space, Tooltip, Typography} from "antd";
import React, {useEffect, useState} from "react";
import MultiSelectWithFetch from "./utils/MultiSelectWithFetch";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import {resourceMapping} from "../../../providers/dataProvider";
import type { DatePickerProps } from "antd";
type RangePickerProps = DatePickerProps;
import {ExclamationCircleOutlined} from "@ant-design/icons";

const { Title, Paragraph } = Typography
const { TextArea } = Input;


const urlGenerator = (resource:string, record_id: number) => {
    return `/${'v1'}/${resourceMapping[resource]}/${record_id}/`
}


function EditMessageView({action} : {action: string}) {
    const { record, loading, loaded } = useEditContext<any>();
    const dataProvider = useDataProvider();
    const [form] = Form.useForm();
    const notify = useNotify();
    const redirect = useRedirect();
    const [selectedTeams, setSelectedTeams] = useState<any>([]);
    const [selectedServices, setSelectedServices] = useState<any>([]);
    const [selectedExpiryDate, setSelectedExpiryDate] = useState<any>(null);
    const [recordConverted, setRecordConverted] = useState<boolean>(false);

    useEffect(() => {
        // Initial Data Conversion
        if(record) {
            if (record.expiry_date)
                setSelectedExpiryDate(dayjs(record.expiry_date).utc())

            if (record?.scopes) {
                const scopes = record.scopes;
                const initialTeams = [];
                const initialServices = [];

                for (let scope_id in scopes) {
                    if (scopes[scope_id]['type'] == "Team") {
                        const team = scopes[scope_id]['scope']
                        initialTeams.push({label: team.name, value: team.id})
                    }
                    else if (scopes[scope_id]['type'] === 'Service') {
                        const service = scopes[scope_id]['scope']
                        initialServices.push({label: service.name, value: service.id})
                    }
                }
                setSelectedTeams(initialTeams);
                setSelectedServices(initialServices);
            }
            setRecordConverted(true)
        }
    }, [record])

    const onSubmit = (values: any) => {
        const finalData: any = {description: values.description}
        if(selectedExpiryDate)
            finalData["expiry_date"] = selectedExpiryDate.toISOString()
        else
            finalData["expiry_date"] = null

        const newScopes:string[] = []
        for(const team in selectedTeams)
            newScopes.push(urlGenerator('teams', selectedTeams[team]['value']))
        for(const service in selectedServices)
            newScopes.push(urlGenerator('services', selectedServices[service]['value']))

        finalData["scopes"] = newScopes
        if(action==="edit")
            dataProvider.update('serviceowner_messages', { id: record.id, data: finalData, previousData: { id: record.id } })
                .then(response => {
                    notify('Message has been updated')
                    redirect('/serviceowner_messages')
                })
                .catch((error: any) => {
                    if (error.status == 400)
                        for (const [key, value] of Object.entries<any>(error.body))
                            form.setFields([{ name: key, errors: value }])
                    else
                        notify("Something went wrong", "warning")
                })

        else if(action==="create")
            dataProvider.create('serviceowner_messages', { data: finalData })
                .then(response => {
                    notify('Message has been created')
                    redirect('/serviceowner_messages')
                })
                .catch((error: any) => {
                    if (error.status == 400)
                        for (const [key, value] of Object.entries<any>(error.body))
                            form.setFields([{ name: key, errors: value }])
                    else
                        notify("Something went wrong", "warning")
                })
    }

    // eslint-disable-next-line arrow-body-style
    const disabledDate: RangePickerProps['disabledDate'] = current => {
        // Can not select days before today and today
        return current && current < dayjs().startOf('day');
    };

    return (
        <>
            {
                (loaded||(loaded==null&&loading==null))?
                    <Form
                        form={form}
                        name="message_form"
                        labelCol={{span: 5}}
                        className="row-col"
                        labelAlign="left"
                        onFinish={onSubmit}
                        style={{margin: "10px"}}
                    >
                        <Form.Item
                            label="Mesage Text"
                            name="description"
                            rules={[{required: true, message: 'Please enter message'}]}
                            initialValue={record?.description}
                        >
                            <TextArea rows={4} placeholder="Meesage"/>
                        </Form.Item>
                        <Form.Item label={"Expiry Date"} name={"expiry_date"}>
                            {/* Create*/}
                            {action==="create" && <DatePicker showTime onChange={(value, dateString) => {setSelectedExpiryDate(value)}} disabledDate={disabledDate}/>}
                            {/* Update */}
                            {action === "edit" && recordConverted && <DatePicker showTime defaultValue={selectedExpiryDate} onChange={(value, dateString) => {setSelectedExpiryDate(value)}} disabledDate={disabledDate}/>}
                        </Form.Item>
                        <Space style={{marginBottom: "5px"}}>
                            <>Visible to the consumers of the</>
                        </Space>
                        {/*<Form.Item label={"team(s)"} help={"Or all teams if no team is selected"}>*/}
                        {/*    <MultiSelectWithFetch fetchResource={"all_teams"} selectedItems={selectedTeams} setSelectedItems={setSelectedTeams}/>*/}
                        {/*</Form.Item>*/}
                        {/*<Form.Item label='services' help={"All teams if no service is selected"}>*/}
                            <Form.Item label={"services"}>
                                <MultiSelectWithFetch fetchResource={"services"} selectedItems={selectedServices} setSelectedItems={setSelectedServices}/>
                                <Tooltip title="If no service is selected, the message will be visible to of the current consumers">
                                    <ExclamationCircleOutlined style={{ color: 'gray', marginLeft: "10px" }} />
                                </Tooltip>
                            </Form.Item>
                        <Form.Item>
                            <Button style={{width: "20%"}} type="primary" htmlType="submit" className="form-button">{record ? "Update" : "Create"}</Button>
                        </Form.Item>
                    </Form>
                    :
                    <></>
            }
        </>
    )

}


const HowItWorks = () => {
    return (
        <Collapse defaultActiveKey={[]} items={[{
            key: '1',
            label: 'How it works?',
            children: (
                <Typography>
                    <Paragraph>
                        Messages are informational notices created by service owners. These messages can be scoped to specific services,
                        and are visible to consumers of those services.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>
                                Each message must include a description that will be shown to consumers.
                            </li>
                            <li>
                                Optionally, an expiry date can be set to automatically hide the message after a specific time.
                            </li>
                            <li>
                                If no services are selected, the message will be visible to all consumers of your services.
                            </li>
                            <li>
                                Consumers will see active messages relevant to their associated services in their dashboard and in submission responses.
                            </li>
                        </ul>
                    </Paragraph>
                </Typography>
            ),
        }]} />
    )
}



const MessageEdit = (props: any) => {
    const history = createHistory();
    return (
        <>
            <Card className=" header-solid h-full ant-card pt-0" bordered={false}>
                <Title level={4}>Message</Title>
                <HowItWorks />
                {
                    props.id?
                        <EditBase  {...props} history={history}>
                            <EditMessageView action={"edit"}/>
                        </EditBase>
                        :
                        <EditMessageView action={"create"}/>
                }
            </Card>
        </>
    )
}
export default MessageEdit;
